/**
 * API client — fetch wrapper with credentials + ApiResponse tipi
 *
 * Aynı origin'de — `/api` prefix'iyle çağrı yapar, cookie otomatik gider.
 */
import type { ApiResponse } from '@swskills/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly httpStatus: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }

  const response = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // session cookie
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // Body'i her zaman dene
  let json: ApiResponse<T> | undefined;
  try {
    json = (await response.json()) as ApiResponse<T>;
  } catch {
    if (!response.ok) {
      throw new ApiError(
        'Beklenmeyen hata oluştu',
        'UNKNOWN_ERROR',
        response.status,
      );
    }
    throw new ApiError(
      'Sunucudan geçersiz yanıt alındı',
      'INVALID_RESPONSE',
      response.status,
    );
  }

  if (!json) throw new ApiError('Boş yanıt', 'EMPTY_RESPONSE', response.status);

  if (!json.success) {
    throw new ApiError(
      json.error.message,
      json.error.code,
      response.status,
      json.error.details,
    );
  }

  return json.data;
}

export const api = {
  get: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T = unknown>(path: string, opts?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};
